import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, Move, MousePointerClick } from "lucide-react";

const TangramGame = () => {
    const navigate = useNavigate();
    const svgRef = useRef(null);
    
    // Standard Tangram pieces with correct proportions
    // For a 200x200 square, each piece is scaled appropriately
    // Pieces are small enough to fit in the square when positioned correctly
    const PIECE_PATHS = {
        smallTriangle: "M 0 0 L 50 0 L 0 50 Z",         // 50x50 right triangle
        mediumTriangle: "M 0 0 L 70.7 0 L 0 70.7 Z",    // 70.7x70.7 right triangle
        largeTriangle: "M 0 0 L 100 0 L 0 100 Z",       // 100x100 right triangle
        square: "M 0 0 L 35.35 0 L 35.35 35.35 L 0 35.35 Z", // 35.35x35.35 square
        parallelogram: "M 0 0 L 35.35 0 L 70.7 35.35 L 35.35 35.35 Z" // parallelogram
    };

    // Initial scattered positions
    const [pieces, setPieces] = useState([
        { id: 0, type: 'smallTriangle', x: 30, y: 30, r: 0, color: '#e74c3c' },
        { id: 1, type: 'smallTriangle', x: 160, y: 30, r: 0, color: '#e67e22' },
        { id: 2, type: 'mediumTriangle', x: 30, y: 150, r: 0, color: '#f1c40f' },
        { id: 3, type: 'largeTriangle', x: 30, y: 320, r: 0, color: '#2ecc71' },
        { id: 4, type: 'largeTriangle', x: 280, y: 320, r: 0, color: '#1abc9c' },
        { id: 5, type: 'square', x: 180, y: 150, r: 0, color: '#3498db' },
        { id: 6, type: 'parallelogram', x: 300, y: 150, r: 0, color: '#9b59b6' }, 
    ]);
    
    const [draggingId, setDraggingId] = useState(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    // Puzzle definitions
    const PUZZLES = {
        square: {
            name: "The Square",
            desc: "Form a perfect square with all 7 pieces.",
            // Target square outline: 200x200 at position (400, 100)
            outline: (
                <rect 
                    x="400" y="100" width="200" height="200" 
                    fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5 3" 
                />
            ),
            // Correct tangram solution for a square
            // Pieces positioned and rotated correctly to form a perfect square
            solution: [
                // Small triangle 1 (Red) - top-left corner
                { id: 0, x: 400, y: 100, r: 0 },
                
                // Small triangle 2 (Orange) - top-right corner
                { id: 1, x: 450, y: 100, r: 90 },
                
                // Medium triangle (Yellow) - left side middle
                { id: 2, x: 400, y: 150, r: 45 },
                
                // Large triangle 1 (Green) - bottom right
                { id: 3, x: 535, y: 235, r: 225 },
                
                // Large triangle 2 (Cyan) - bottom left
                { id: 4, x: 400, y: 235, r: 315 },
                
                // Square (Blue) - center
                { id: 5, x: 432.5, y: 167.5, r: 45 },
                
                // Parallelogram (Purple) - right center
                { id: 6, x: 470.7, y: 167.5, r: 45 }
            ]
        },
        house: {
            name: "The House",
            desc: "A cozy place. Can you build the roof and walls?",
            outline: (
                <path 
                    d="M 400 350 L 500 350 L 450 280 Z M 420 350 L 420 420 L 480 420 L 480 350 Z" 
                    fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5 3" 
                />
            ),
            solution: []
        },
        swan: {
            name: "The Swan",
            desc: "Graceful and elegant.",
            outline: (
                <path 
                    d="M 380 350 L 520 350 L 480 420 Z M 380 350 L 340 300 L 380 320 Z" 
                    fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5 3" 
                />
            ),
            solution: []
        }
    };

    const [currentPuzzle, setCurrentPuzzle] = useState("square");

    const handleMouseDown = (e, id) => {
        e.preventDefault();
        const piece = pieces.find(p => p.id === id);
        const svg = svgRef.current.getBoundingClientRect();
        const mx = e.clientX - svg.left;
        const my = e.clientY - svg.top;
        
        setDraggingId(id);
        setOffset({ x: mx - piece.x, y: my - piece.y });
    };
    
    const handleMouseMove = (e) => {
        if (draggingId === null) return;
        
        const svg = svgRef.current.getBoundingClientRect();
        const mx = e.clientX - svg.left;
        const my = e.clientY - svg.top;
        
        setPieces(pieces.map(p => {
            if (p.id === draggingId) {
                return { ...p, x: mx - offset.x, y: my - offset.y };
            }
            return p;
        }));
    };
    
    const handleMouseUp = () => {
        if (draggingId !== null) {
            setPieces(pieces.map(p => {
                if (p.id === draggingId) {
                    const snap = 5;
                    return { 
                        ...p, 
                        x: Math.round(p.x / snap) * snap, 
                        y: Math.round(p.y / snap) * snap 
                    };
                }
                return p;
            }));
        }
        setDraggingId(null);
    };
    
    const handleDoubleClick = (id) => {
        setPieces(pieces.map(p => {
            if (p.id === id) {
                return { ...p, r: (p.r + 45) % 360 };
            }
            return p;
        }));
    };
    
    const handleShowSolution = () => {
        if (PUZZLES[currentPuzzle].solution.length > 0) {
            if (window.confirm("Show solution? This will reset your progress.")) {
                const sol = PUZZLES[currentPuzzle].solution;
                const newPieces = pieces.map(p => {
                    const match = sol.find(s => s.id === p.id);
                    return match ? { ...p, ...match } : p;
                });
                setPieces(newPieces);
            }
        } else {
            alert("Solution coming soon for this shape!");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8">
            <div className="w-full max-w-4xl px-4 flex justify-between items-center mb-6">
                 <button onClick={() => navigate("/")} className="flex items-center text-gray-600 hover:text-gray-900">
                    <ArrowLeft className="mr-1" size={20} /> Back
                 </button>
                 <h1 className="text-3xl font-bold text-gray-800">Tangram Puzzle</h1>
                 <button onClick={() => window.location.reload()} className="text-blue-600 hover:text-blue-800">
                    <RotateCcw size={20} />
                 </button>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg border relative overflow-hidden">
                <p className="text-center text-gray-600 mb-4 flex items-center justify-center gap-3">
                    <Move className="w-4 h-4" /> Drag to move 
                    <span className="text-gray-300">|</span> 
                    <MousePointerClick className="w-4 h-4" /> Double click to rotate (45°)
                </p>
                <div className="absolute top-6 left-6 bg-blue-50 p-3 rounded-lg border border-blue-200 max-w-xs z-10 pointer-events-none select-none">
                    <h3 className="font-bold text-blue-900 text-sm mb-1">Goal: {PUZZLES[currentPuzzle].name}</h3>
                    <p className="text-xs text-blue-800">
                        {PUZZLES[currentPuzzle].desc}
                    </p>
                </div>
                <div className="absolute top-6 right-6 z-10">
                     <button 
                        onClick={handleShowSolution}
                        className={`
                            px-4 py-2 rounded text-sm font-bold shadow transition-colors
                            ${PUZZLES[currentPuzzle].solution.length > 0 
                                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer' 
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
                        `}
                     >
                         Show Solution
                     </button>
                </div>
                <svg 
                    ref={svgRef}
                    width="800" 
                    height="500" 
                    className="bg-gray-50 border rounded cursor-crosshair touch-none"
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {/* Grid background */}
                    <defs>
                        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="gray" strokeWidth="0.3" opacity="0.15"/>
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                    
                    {/* Target Area */}
                    {PUZZLES[currentPuzzle].outline}

                    {/* Pieces */}
                    {pieces.map(p => (
                        <g 
                            key={p.id} 
                            transform={`translate(${p.x}, ${p.y}) rotate(${p.r})`}
                            onMouseDown={(e) => handleMouseDown(e, p.id)}
                            onDoubleClick={() => handleDoubleClick(p.id)}
                            style={{ cursor: 'grab' }}
                        >
                            <path 
                                d={PIECE_PATHS[p.type]} 
                                fill={p.color} 
                                stroke="white" 
                                strokeWidth="1.5"
                                opacity={draggingId === p.id ? 0.85 : 1}
                                className="transition-opacity hover:opacity-85"
                            />
                            {/* Center indicator */}
                            <circle cx="0" cy="0" r="2" fill="rgba(0,0,0,0.3)" pointerEvents="none" />
                        </g>
                    ))}
                </svg>
            </div>
            
            <div className="mt-8 max-w-2xl text-center text-gray-700">
                <p className="mb-4 font-semibold">Select a puzzle shape:</p>
                <div className="flex justify-center gap-4">
                    {Object.keys(PUZZLES).map(key => (
                         <button
                            key={key}
                            onClick={() => setCurrentPuzzle(key)}
                            className={`
                                w-28 py-3 flex items-center justify-center text-sm font-bold rounded-lg border-2 transition-all
                                ${currentPuzzle === key 
                                    ? 'bg-blue-600 text-white border-blue-700 shadow-md scale-105' 
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-400'
                                }
                            `}
                         >
                             {PUZZLES[key].name}
                         </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TangramGame;
